import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { savingPotAPI } from '@/lib/api';
import { 
  PiggyBank, Wallet, DollarSign, Users, Check, X, Clock,
  Loader2, ChevronRight, History, ArrowUpRight, Send, Eye
} from 'lucide-react';

export default function StaffSavingPotManager({ user }) {
  const [loading, setLoading] = useState(true);
  const [savingPotsData, setSavingPotsData] = useState(null);
  const [payoutRequests, setPayoutRequests] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [employeeDetails, setEmployeeDetails] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [showPayoutDialog, setShowPayoutDialog] = useState(false);
  const [payoutNotes, setPayoutNotes] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [potsRes, requestsRes] = await Promise.all([
        savingPotAPI.getAllSavingPots(),
        savingPotAPI.getPayoutRequests('pending')
      ]);
      setSavingPotsData(potsRes.data);
      setPayoutRequests(requestsRes.data || []);
    } catch (error) {
      console.error('Error fetching saving pot data:', error);
      toast.error('Failed to load saving pot data');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = async (employee) => {
    setSelectedEmployee(employee);
    try {
      const res = await savingPotAPI.getEmployeeSavingPot(employee.employee_id);
      setEmployeeDetails(res.data);
      setShowDetails(true);
    } catch (error) {
      toast.error('Failed to load employee details');
    }
  };

  const handleProcessRequest = async (requestId, action) => {
    try {
      setProcessing(true);
      await savingPotAPI.processPayoutRequest(requestId, action, payoutNotes || null);
      toast.success(action === 'approve' ? 'Payout approved!' : 'Request rejected');
      setPayoutNotes('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process request');
    } finally {
      setProcessing(false);
    }
  };

  const handleDirectPayout = async (employeeId) => {
    if (!confirm('Are you sure you want to process this payout?')) return;
    
    try {
      setProcessing(true);
      const res = await savingPotAPI.directPayout(employeeId, null, payoutNotes || 'Director-initiated payout');
      toast.success(res.data.message);
      setShowPayoutDialog(false);
      setPayoutNotes('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process payout');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
        <span className="ml-3 text-gray-600 dark:text-gray-300">Loading saving pot data...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="saving-pot-manager">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-teal-500 to-teal-600 text-white border-0">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-teal-100 text-sm">Total Credited</p>
                <p className="text-2xl font-bold">Rs. {savingPotsData?.summary?.total_credited?.toLocaleString() || 0}</p>
              </div>
              <PiggyBank className="w-10 h-10 opacity-80" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white border-0">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100 text-sm">Total Paid Out</p>
                <p className="text-2xl font-bold">Rs. {savingPotsData?.summary?.total_paid_out?.toLocaleString() || 0}</p>
              </div>
              <ArrowUpRight className="w-10 h-10 opacity-80" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-orange-500 to-orange-600 text-white border-0">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-orange-100 text-sm">Pending Balance</p>
                <p className="text-2xl font-bold">Rs. {savingPotsData?.summary?.total_pending_balance?.toLocaleString() || 0}</p>
              </div>
              <Wallet className="w-10 h-10 opacity-80" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white border-0">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm">Active Employees</p>
                <p className="text-2xl font-bold">{savingPotsData?.summary?.total_employees || 0}</p>
              </div>
              <Users className="w-10 h-10 opacity-80" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Pending Payout Requests */}
      {payoutRequests.length > 0 && (
        <Card className="border-yellow-200 dark:border-yellow-800 bg-yellow-50 dark:bg-yellow-900/20">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2 text-yellow-800 dark:text-yellow-200">
              <Clock className="w-5 h-5" />
              Pending Payout Requests ({payoutRequests.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {payoutRequests.map((request) => (
                <div key={request.id} className="flex items-center justify-between p-4 bg-white dark:bg-slate-800 rounded-lg border border-yellow-200 dark:border-yellow-800">
                  <div>
                    <p className="font-medium text-gray-800 dark:text-white">{request.employee_name}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Requested: Rs. {request.requested_amount?.toLocaleString()} • {new Date(request.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      className="bg-green-600 hover:bg-green-700 text-white"
                      onClick={() => handleProcessRequest(request.id, 'approve')}
                      disabled={processing}
                    >
                      <Check className="w-4 h-4 mr-1" /> Approve
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-red-500 text-red-600 hover:bg-red-50"
                      onClick={() => handleProcessRequest(request.id, 'reject')}
                      disabled={processing}
                    >
                      <X className="w-4 h-4 mr-1" /> Reject
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* All Employees Saving Pots */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <PiggyBank className="w-5 h-5 text-teal-600" />
            Staff Saving Pots
          </CardTitle>
        </CardHeader>
        <CardContent>
          {savingPotsData?.employees?.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-slate-700">
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-600 dark:text-gray-300">Employee</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-600 dark:text-gray-300">Department</th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-gray-600 dark:text-gray-300">Total Credited</th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-gray-600 dark:text-gray-300">Paid Out</th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-gray-600 dark:text-gray-300">Balance</th>
                    <th className="text-center py-3 px-4 text-sm font-medium text-gray-600 dark:text-gray-300">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {savingPotsData.employees.map((emp) => (
                    <tr key={emp.employee_id} className="border-b border-gray-100 dark:border-slate-700 hover:bg-gray-50 dark:hover:bg-slate-800">
                      <td className="py-3 px-4">
                        <div>
                          <p className="font-medium text-gray-800 dark:text-white">{emp.employee_name}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">{emp.employee_email}</p>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-gray-600 dark:text-gray-300">{emp.department}</td>
                      <td className="py-3 px-4 text-right text-gray-600 dark:text-gray-300">
                        Rs. {emp.total_credited?.toLocaleString()}
                      </td>
                      <td className="py-3 px-4 text-right text-green-600">
                        Rs. {emp.total_paid_out?.toLocaleString()}
                      </td>
                      <td className="py-3 px-4 text-right">
                        <span className={`font-bold ${emp.current_balance > 0 ? 'text-orange-600' : 'text-gray-400'}`}>
                          Rs. {emp.current_balance?.toLocaleString()}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex justify-center gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleViewDetails(emp)}
                            data-testid={`view-pot-${emp.employee_id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          {emp.current_balance > 0 && user?.is_super_admin && (
                            <Dialog>
                              <DialogTrigger asChild>
                                <Button
                                  size="sm"
                                  className="bg-teal-600 hover:bg-teal-700 text-white"
                                  onClick={() => setSelectedEmployee(emp)}
                                  data-testid={`payout-btn-${emp.employee_id}`}
                                >
                                  <Send className="w-4 h-4" />
                                </Button>
                              </DialogTrigger>
                              <DialogContent>
                                <DialogHeader>
                                  <DialogTitle>Process Payout - {emp.employee_name}</DialogTitle>
                                </DialogHeader>
                                <div className="space-y-4 mt-4">
                                  <div className="p-4 bg-teal-50 dark:bg-teal-900/30 rounded-lg">
                                    <p className="text-sm text-gray-600 dark:text-gray-300">Current Balance</p>
                                    <p className="text-2xl font-bold text-teal-600">Rs. {emp.current_balance?.toLocaleString()}</p>
                                  </div>
                                  <div>
                                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Notes (Optional)</label>
                                    <Textarea
                                      value={payoutNotes}
                                      onChange={(e) => setPayoutNotes(e.target.value)}
                                      placeholder="Add any notes for this payout..."
                                      rows={2}
                                    />
                                  </div>
                                  <Button
                                    onClick={() => handleDirectPayout(emp.employee_id)}
                                    disabled={processing}
                                    className="w-full bg-teal-600 hover:bg-teal-700"
                                  >
                                    {processing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                                    Process Payout
                                  </Button>
                                </div>
                              </DialogContent>
                            </Dialog>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              <PiggyBank className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No saving pot entries yet</p>
              <p className="text-sm mt-1">Entries are created when payments ≥ Rs. 25,000 are recorded</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Employee Details Dialog */}
      <Dialog open={showDetails} onOpenChange={setShowDetails}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PiggyBank className="w-5 h-5 text-teal-600" />
              Saving Pot History - {employeeDetails?.employee?.full_name}
            </DialogTitle>
          </DialogHeader>
          
          {employeeDetails && (
            <div className="space-y-4 mt-4">
              {/* Summary */}
              <div className="grid grid-cols-3 gap-3">
                <div className="p-3 bg-teal-50 dark:bg-teal-900/30 rounded-lg text-center">
                  <p className="text-xs text-gray-500 dark:text-gray-400">Total Credited</p>
                  <p className="text-lg font-bold text-teal-600">Rs. {employeeDetails.total_credited?.toLocaleString()}</p>
                </div>
                <div className="p-3 bg-green-50 dark:bg-green-900/30 rounded-lg text-center">
                  <p className="text-xs text-gray-500 dark:text-gray-400">Paid Out</p>
                  <p className="text-lg font-bold text-green-600">Rs. {employeeDetails.total_paid_out?.toLocaleString()}</p>
                </div>
                <div className="p-3 bg-orange-50 dark:bg-orange-900/30 rounded-lg text-center">
                  <p className="text-xs text-gray-500 dark:text-gray-400">Balance</p>
                  <p className="text-lg font-bold text-orange-600">Rs. {employeeDetails.current_balance?.toLocaleString()}</p>
                </div>
              </div>

              {/* Credit Entries */}
              <div>
                <h4 className="font-medium text-gray-800 dark:text-white mb-2 flex items-center gap-2">
                  <History className="w-4 h-4" /> Credit History ({employeeDetails.entries?.length || 0})
                </h4>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {employeeDetails.entries?.map((entry, idx) => (
                    <div key={entry.id || idx} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-slate-700 rounded-lg text-sm">
                      <div>
                        <p className="font-medium text-gray-800 dark:text-white">Rs. {entry.pot_credit?.toLocaleString()}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          From: {entry.student_name} • Payment: Rs. {entry.payment_amount?.toLocaleString()}
                        </p>
                      </div>
                      <div className="text-right">
                        <Badge className={entry.status === 'credited' ? 'bg-orange-100 text-orange-700' : 'bg-green-100 text-green-700'}>
                          {entry.status === 'credited' ? 'Pending' : 'Paid Out'}
                        </Badge>
                        <p className="text-xs text-gray-500 mt-1">{new Date(entry.created_at).toLocaleDateString()}</p>
                      </div>
                    </div>
                  ))}
                  {(!employeeDetails.entries || employeeDetails.entries.length === 0) && (
                    <p className="text-center text-gray-500 py-4">No credit entries yet</p>
                  )}
                </div>
              </div>

              {/* Payout History */}
              {employeeDetails.payouts?.length > 0 && (
                <div>
                  <h4 className="font-medium text-gray-800 dark:text-white mb-2 flex items-center gap-2">
                    <ArrowUpRight className="w-4 h-4" /> Payout History
                  </h4>
                  <div className="space-y-2">
                    {employeeDetails.payouts.map((payout, idx) => (
                      <div key={payout.id || idx} className="flex items-center justify-between p-3 bg-green-50 dark:bg-green-900/20 rounded-lg text-sm">
                        <div>
                          <p className="font-medium text-green-700 dark:text-green-300">Rs. {payout.amount?.toLocaleString()}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">By: {payout.processed_by_name}</p>
                        </div>
                        <p className="text-xs text-gray-500">{new Date(payout.created_at).toLocaleDateString()}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
